import * as ToastPrimitive from '@radix-ui/react-toast';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircleBroken,
  InfoCircle,
  XClose,
} from '@untitled-ui/icons-react';
import { twMerge } from 'tailwind-merge';

import { tGlobal } from '../services/translation';
import { Button } from './buttons/button';
import { IconButton } from './buttons/icon-button';
import { FeaturedIcon } from './featured-icon';
import { Loading } from './loading';

export enum ToastVariant {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
  Loading = 'loading',
}

export type ToastItem = {
  title: string;
  description?: string;
  cta?: {
    text: string;
    onClick: () => unknown;
    closeOnClick?: boolean;
  };
  variant?: ToastVariant;
  className?: string;
  hideCrossClose?: boolean;
  descriptionClassName?: string;
  duration?: number;
  onOpenChange?: (open: boolean) => unknown;
  open?: boolean;
  id: number;
};

export type ToastProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  toasts: ToastItem[];
  onToastClose: (id: number) => unknown;
};

function ToastIcon(props: { variant: ToastVariant }) {
  if (props.variant === ToastVariant.Loading) {
    return <Loading size="md" className="text-branding-accent" />;
  }
  if (props.variant === ToastVariant.Info) {
    return <FeaturedIcon icon={<InfoCircle />} color="gray" size="sm" />;
  }
  if (props.variant === ToastVariant.Success) {
    return <FeaturedIcon icon={<CheckCircleBroken />} color="green" size="sm" />;
  }
  if (props.variant === ToastVariant.Warning) {
    return <FeaturedIcon icon={<AlertTriangle />} color="yellow" size="sm" />;
  }
  return <FeaturedIcon icon={<AlertCircle />} color="red" size="sm" />;
}

function OneToast(props: ToastItem & { onToastClose?: (id: number) => unknown }) {
  const {
    id,
    open,
    title,
    description,
    variant = ToastVariant.Info,
    className = '',
    cta,
    descriptionClassName = '',
  } = props;
  const rootClass = twMerge([
    'flex w-full max-w-[350px] items-center justify-between rounded-lg border p-3 shadow-toast bg-white gap-2',
    className,
  ]);
  const nonLoadingDuration = cta == null ? 3000 : 6000;
  const duration = variant === ToastVariant.Loading ? Infinity : nonLoadingDuration;
  return (
    <ToastPrimitive.Root
      open={open}
      onOpenChange={function () {
        if (typeof props.onToastClose !== 'function') {
          return;
        }
        props.onToastClose(id);
      }}
      duration={props.duration ?? duration}
      className={rootClass}
      key={id}
    >
      <div className="flex flex-1 items-center gap-4">
        <ToastIcon variant={variant} />
        <div className="flex flex-1 flex-col gap-1">
          <ToastPrimitive.Title className="font-body-2-sb whitespace-wrap overflow-hidden text-ellipsis">
            {title}
          </ToastPrimitive.Title>
          {description != null ? (
            <ToastPrimitive.Description
              className={twMerge([
                'font-body-2-r line-clamp-2 overflow-hidden text-ellipsis text-branding-gray',
                descriptionClassName,
              ])}
            >
              {description}
            </ToastPrimitive.Description>
          ) : null}
        </div>
      </div>
      <div className="flex items-center justify-between gap-1">
        {cta != null ? (
          <Button
            variant="ghost-accent"
            size="sm"
            onClick={function () {
              cta.onClick();

              if (props.cta?.closeOnClick && typeof props.onToastClose === 'function') {
                props.onToastClose(id);
              }
            }}
          >
            {cta.text}
          </Button>
        ) : null}
        {variant === ToastVariant.Loading || props.hideCrossClose ? null : (
          <ToastPrimitive.Close aria-label={tGlobal('close')}>
            <IconButton isDiv icon={XClose} variant="ghost-neutral" />
          </ToastPrimitive.Close>
        )}
      </div>
    </ToastPrimitive.Root>
  );
}

export function Toast(props: ToastProps) {
  const { toasts = [] } = props;
  const toastsWithLimit = toasts.slice(-3);

  return (
    <ToastPrimitive.Provider>
      {toastsWithLimit.map(function (toast) {
        return <OneToast key={toast.id} {...toast} onToastClose={props.onToastClose} />;
      })}
      <div className="fixed bottom-0 left-0 z-100 w-full">
        <ToastPrimitive.Viewport className="absolute bottom-10 left-1/2 m-0 flex w-[700px] -translate-x-1/2 list-none flex-col items-center gap-2 p-6" />
      </div>
    </ToastPrimitive.Provider>
  );
}
